/**
 * Reading a Google Places result into the parts a deal record keeps.
 *
 * Pure, and deliberately not in the component file: the address box is one
 * caller, and the same parsing is what any other address surface would need.
 */

export type ResolvedAddress = {
  /** The whole thing, as Google formats it. */
  formatted: string
  street: string
  city: string
  state: string
  postalCode: string
  country: string
  lat: number | null
  lng: number | null
}

/** Pull the parts out of a Places result. */
export function readAddressComponents(
  place: google.maps.places.PlaceResult | { address_components?: unknown; formatted_address?: string; geometry?: unknown },
): ResolvedAddress {
  const components = (place as { address_components?: google.maps.GeocoderAddressComponent[] })
    .address_components ?? []
  const pick = (type: string, short = false): string => {
    const hit = components.find((c) => c.types?.includes(type))
    if (!hit) return ''
    return (short ? hit.short_name : hit.long_name) ?? ''
  }
  // Google splits a street address in two: the house number and the road.
  const street = [pick('street_number'), pick('route')].filter(Boolean).join(' ')
  const geometry = (place as { geometry?: { location?: { lat?: () => number; lng?: () => number } } }).geometry
  const loc = geometry?.location
  const lat = typeof loc?.lat === 'function' ? loc.lat() : null
  const lng = typeof loc?.lng === 'function' ? loc.lng() : null

  return {
    formatted: (place as { formatted_address?: string }).formatted_address ?? '',
    street,
    // A city is `locality` in most of the US, but a good deal of commercial
    // property sits in unincorporated county land or a township, where Google
    // returns only the sublocality or the administrative level below the state.
    city: pick('locality') || pick('sublocality') || pick('postal_town') || pick('administrative_area_level_3'),
    state: pick('administrative_area_level_1', true),
    // Zip+4 arrives as two components; the deal record wants the five-digit one.
    postalCode: pick('postal_code'),
    country: pick('country', true),
    lat: Number.isFinite(lat) ? (lat as number) : null,
    lng: Number.isFinite(lng) ? (lng as number) : null,
  }
}
